import { useMemo, useState } from 'react';

import { useId } from '../../../util/styling';
import type { EditorContextOptions } from '../editorContext';
import type { HostContextOptions } from '../hostContext';
import { elementIdFromSuggestionListAndItemId } from '../hostContext';

interface AutocompleteAriaHostState {
  readonly hostContextValue: HostContextOptions;
  readonly editorContextValue: EditorContextOptions;
}

type UseAutocompleteAriaHostState = () => AutocompleteAriaHostState;
export const useAutocompleteAriaHostState: UseAutocompleteAriaHostState = () => {
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

  const suggestionListId = useId('suggestionListId');

  const hostContextValue = useMemo(() => {
    const setIsClosed = () => {
      setSelectedItemId(undefined);
      setIsAutocompleteOpen(false);
    };

    const setIsLoading = () => {
      setSelectedItemId(undefined);
      setIsAutocompleteOpen(true);
    };

    const setIsOpen = (selectedItemId?: string) => {
      setSelectedItemId(selectedItemId);
      setIsAutocompleteOpen(true);
    };

    return {
      setIsClosed,
      setIsOpen,
      setIsLoading,
      suggestionListId,
    };
  }, [suggestionListId]);

  const ariaAutocomplete = 'list';
  const ariaControls = isAutocompleteOpen ? suggestionListId : undefined;
  const ariaActiveDescendant = selectedItemId
    ? elementIdFromSuggestionListAndItemId(suggestionListId, selectedItemId)
    : undefined;

  const editorContextValue = useMemo<EditorContextOptions>(
    () => ({
      'aria-autocomplete': ariaAutocomplete,
      'aria-controls': ariaControls,
      'aria-activedescendant': ariaActiveDescendant,
    }),
    [ariaControls, ariaActiveDescendant]
  );

  return {
    hostContextValue,
    editorContextValue,
  };
};
